/**
 * Chained attacks.
 *
 * Swinging again soon after a swing continues the combo; letting the window
 * lapse starts over. The steps escalate so that pressing rhythmically beats
 * mashing at random — the finisher is worth reaching, and reaching it is a
 * thing the player does rather than a thing that happens.
 */

/** How long after a swing another one still counts as a continuation. */
export const COMBO_WINDOW = 0.55;

export interface ComboStep {
  /** Multiplies the weapon's damage. */
  readonly damage: number;
  /** Multiplies the weapon's swing time. */
  readonly duration: number;
  /** Multiplies the weapon's reach. */
  readonly reach: number;
}

/** Three steps: two quick cuts and a heavier finisher. */
export const COMBO_STEPS: readonly ComboStep[] = [
  { damage: 1, duration: 1, reach: 1 },
  { damage: 1.15, duration: 0.95, reach: 1 },
  { damage: 1.6, duration: 1.35, reach: 1.3 },
];

export class Combo {
  private index = -1;
  private timer = 0;

  update(dt: number): void {
    if (this.timer <= 0) return;
    this.timer = Math.max(0, this.timer - dt);
    if (this.timer === 0) this.index = -1;
  }

  /** Take the next step, wrapping back to the first after the finisher. */
  advance(): ComboStep {
    this.index = this.timer > 0 ? (this.index + 1) % COMBO_STEPS.length : 0;
    this.timer = COMBO_WINDOW;
    return COMBO_STEPS[this.index]!;
  }

  /** 0-based step, or -1 when no combo is running. */
  get step(): number {
    return this.timer > 0 ? this.index : -1;
  }
}
